/**
 * costumeEvolution.ts — which forms can evolve, and into what (2026-09-15
 * owner report: the destiny pick showed Raichu on a costume Pikachu).
 *
 * GO's rule: a costumed Pokémon evolves only into the SAME costume on the
 * next stage, and only when that costumed evolution exists (Holiday Vulpix
 * becomes Holiday Ninetales; a World Championships Pikachu never becomes
 * anything). The catalogue already says which costumed evolutions exist, so
 * the answer is a lookup, not a curated list: a costume can evolve into a
 * species when that species carries a costume with the same suffix.
 * Regional and other non-costume forms keep the whole species chain.
 */
import { FORM_GROUPS_BY_FORM_ID, FORM_GROUPS_BY_SPECIES_ID } from "./generated/pokemonForms.generated";
import { evolutionChainFor } from "./generated/speciesBaseStats.generated";

/** The costume part of a form id ("pikachu-wcs-2024" → "wcs-2024"), or null for a non-costume form. */
export function costumeSuffix(formId: string | null | undefined): string | null {
  if (!formId) return null;
  const group = FORM_GROUPS_BY_FORM_ID.get(formId);
  const form = group?.forms.find((f) => f.formId === formId);
  if (!group || !form || form.kind !== "costume") return null;
  const base = `${group.speciesName}-`;
  if (formId.startsWith(base)) return formId.slice(base.length);
  const dash = formId.indexOf("-");
  return dash > 0 ? formId.slice(dash + 1) : null;
}

/** Can a Pokémon in this form evolve into `evolvedSpeciesId` and keep being itself? */
export function formCanEvolveTo(formId: string | null | undefined, evolvedSpeciesId: number): boolean {
  const suffix = costumeSuffix(formId);
  if (suffix == null) return true;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(evolvedSpeciesId);
  return !!group?.forms.some((f) => f.kind === "costume" && f.formId.endsWith(`-${suffix}`));
}

/**
 * Evolutions only one gender can reach (2026-09-20 owner report: a male
 * Combee or Salandit offered Vespiquen / Salazzle as its destiny). GO's
 * rules: Vespiquen and Salazzle are female-only, Gallade is male-only
 * (Gardevoir is open to both), Froslass is female-only (Glalie is open),
 * Burmy splits: Wormadam female, Mothim male. Keyed by the EVOLVED species.
 */
export const GENDER_LOCKED_EVOLUTIONS: Readonly<Record<number, "male" | "female">> = {
  416: "female", // Vespiquen (from Combee)
  758: "female", // Salazzle (from Salandit)
  475: "male", // Gallade (from Kirlia)
  478: "female", // Froslass (from Snorunt)
  413: "female", // Wormadam (from Burmy)
  414: "male", // Mothim (from Burmy)
};

/** Can a Pokémon of this gender evolve into `evolvedSpeciesId`? Unknown gender can. */
export function genderCanEvolveTo(gender: "male" | "female" | null | undefined, evolvedSpeciesId: number): boolean {
  const lock = GENDER_LOCKED_EVOLUTIONS[evolvedSpeciesId];
  return lock == null || gender == null || gender === lock;
}

/**
 * The species chain above this form: every stage for an ordinary form,
 * only costumed stages for a costume, and only the stages this gender can
 * reach when the gender is known.
 */
export function evolutionChainForForm(
  speciesId: number,
  formId: string | null | undefined,
  gender?: "male" | "female" | null,
): number[] {
  const chain = evolutionChainFor(speciesId).filter((id) => genderCanEvolveTo(gender, id));
  if (costumeSuffix(formId) == null) return chain;
  return chain.filter((id) => formCanEvolveTo(formId, id));
}

/** The form part of ANY form id ("geodude-alola" → "alola", "pikachu-wcs-2024" → "wcs-2024"), or null for a base form / unknown id. */
export function formSuffix(formId: string | null | undefined): string | null {
  if (!formId) return null;
  const group = FORM_GROUPS_BY_FORM_ID.get(formId);
  const form = group?.forms.find((f) => f.formId === formId);
  if (!group || !form) return null;
  const base = `${group.speciesName}-`;
  if (formId.startsWith(base)) return formId.slice(base.length);
  const dash = formId.indexOf("-");
  return dash > 0 ? formId.slice(dash + 1) : null;
}

/**
 * The form the evolved stage keeps (2026-09-15 "Evolved it?" button):
 * Alolan Geodude becomes Alolan Graveler, Holiday Vulpix becomes Holiday
 * Ninetales. Null when the evolved species has no such form (a regional
 * line that merges back into the ordinary form, or a base-form record).
 */
export function evolvedFormIdFor(formId: string | null | undefined, evolvedSpeciesId: number): string | null {
  const suffix = formSuffix(formId);
  if (suffix == null) return null;
  const group = FORM_GROUPS_BY_SPECIES_ID.get(evolvedSpeciesId);
  return group?.forms.find((f) => f.formId.endsWith(`-${suffix}`))?.formId ?? null;
}
